// src/pages/Home.js
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { motion } from "framer-motion";
import { ClipboardList, FileText, PackageSearch, Smile, PiggyBank, HandCoins, Fuel, ShoppingCart, PlusCircle, FilePlus, UserPlus, Cog, PackagePlus, BarChart3 } from "lucide-react";
import axiosInstance from "../services/axiosInstance";
import { handleApiError } from "../components/apiErrorHandler";
import SummaryDialog from "../components/SummaryDialog";
import QuickReportViewer from "../components/QuickReportViewer";
import { QUICK_REPORTS, defaultChoice, reportParams } from "../components/quickReports";
import { ProductSelectorInput } from "../components/ProductSelectorInput";
import { UserContext, usePermission } from "../contexts/UserContext";

// ── Permission map for quick links ────────────────────────────────────────────
const QUICK_LINK_PERMISSIONS = {
    customers:         'view_customers',
    prescriptions:     'view_prescriptions',
    invoices:          'view_invoices',
    customer_payments: 'view_payments',
    refill_reminders:  'view_refill_reminders',
    inventory:         'view_inventory',
    move_orders:       'view_move_orders',
    supplier_payment:  'view_supplier_payments',
};

const ALL_QUICK_LINKS = [
    { key: "customers",         label: "Customers",         icon: Smile,         path: "/customers" },
    { key: "prescriptions",     label: "Prescriptions",     icon: ClipboardList, path: "/prescriptions" },
    { key: "invoices",          label: "Invoices",          icon: FileText,      path: "/invoices" },
    { key: "customer_payments", label: "Customer Payments", icon: PiggyBank,     path: "/customer-payments" },
    { key: "refill_reminders",  label: "Refill Reminders",  icon: Fuel,          path: "/refill-reminders" },
    { key: "inventory",         label: "Inventory",         icon: PackageSearch, path: "/inventory" },
    { key: "move_orders",       label: "Move Orders",       icon: ShoppingCart,  path: "/moveorders" },
    { key: "supplier_payment",  label: "Supplier Payments", icon: HandCoins,     path: "/supplier-payments" },
];

// ── Quick search options with their required permissions ──────────────────────
const ALL_SEARCH_OPTIONS = [
    { label: "Customer",             value: "customer",  permission: 'view_customers' },
    { label: "Invoice Number",       value: "invoice",   permission: 'view_invoices'  },
    { label: "Inventory (Product)",  value: "inventory", permission: 'view_inventory' },
];

// ── Quick tasks with their required permissions ───────────────────────────────
const ALL_QUICK_TASKS = [
    {
        key:        'create_customer',
        label:      'Create Customer',
        icon:       UserPlus,
        permission: 'manage_customers',
        route:      '/customers',
        state:      { openCreate: true },
    },
    {
        key:        'create_prescription',
        label:      'Create Prescription',
        icon:       FilePlus,
        permission: 'manage_prescriptions',
        route:      '/prescriptions',
        state:      { openCreate: true },
    },
    {
        key:        'create_invoice',
        label:      'Create Invoice',
        icon:       PlusCircle,
        permission: 'create_invoices',
        route:      '/invoices',
        state:      { openCreate: true },
    },
    {
        key:        'create_grn',
        label:      'Create GRN',
        icon:       PackagePlus,
        permission: 'manage_move_orders',
        route:      '/moveorders',
        state:      { openCreate: true },
    },
];

// ──  "not available" section for Quick Summary ─────────────────────
const RestrictedSection = () => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1.5rem',
            gap: '0.75rem',
            textAlign: 'center',
        }}
    >
        <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #e0e7ff 0%, #f0fdf4 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.75rem',
            boxShadow: '0 2px 12px rgba(99,102,241,0.10)',
        }}>
            🔒
        </div>
        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', color: '#374151' }}>
            This section is not available for your responsibilities
        </p>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af', maxWidth: '220px', lineHeight: 1.5 }}>
            Contact your pharmacy admin if you need access to reports and summaries.
        </p>
    </motion.div>
);

// ── Hook: returns Set of permitted quick link keys ────────────────────────────
const useAllowedQuickLinks = () => {
    const user = useContext(UserContext);
    if (!user?.permissions) return new Set();
    if (user.permissions.includes('*')) return new Set(ALL_QUICK_LINKS.map(l => l.key));
    const allowed = new Set();
    ALL_QUICK_LINKS.forEach(link => {
        const required = QUICK_LINK_PERMISSIONS[link.key];
        if (!required || user.permissions.includes(required)) allowed.add(link.key);
    });
    return allowed;
};

// ── Hook: returns permitted search options and quick tasks ────────────────────
const useAllowedSearchAndTasks = () => {
    const user = useContext(UserContext);
    if (!user?.permissions) return { searchOptions: [], quickTasks: [] };
    const isAdmin = user.permissions.includes('*');

    const searchOptions = ALL_SEARCH_OPTIONS.filter(
        opt => isAdmin || user.permissions.includes(opt.permission)
    );
    const quickTasks = ALL_QUICK_TASKS.filter(
        task => isAdmin || user.permissions.includes(task.permission)
    );
    return { searchOptions, quickTasks };
};

// ─────────────────────────────────────────────────────────────────────────────

const Home = () => {
    const [customerValue, setCustomerValue]         = useState(null);
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [invoiceValue, setInvoiceValue]           = useState(null);
    const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
    const [searchResult, setSearchResult]   = useState(null);
    const [summaryVisible, setSummaryVisible]   = useState(false);
    const [hasSearched, setHasSearched]         = useState(false);
    const [productQuery, setProductQuery]       = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [visibleQuickLinks, setVisibleQuickLinks] = useState([]);
    const [customizeVisible, setCustomizeVisible]   = useState(false);
    const [tempQuickLinks, setTempQuickLinks]       = useState([]);
    const [department, setDepartment]               = useState(null);
    const navigate = useNavigate();

    // Permissions
    const canViewSummary     = usePermission('view_quick_summary');
    const canDownloadReports = usePermission('download_reports');
    const canViewReports     = usePermission('view_reports');
    const [openReport, setOpenReport] = useState(null);       // the Quick Report open in the on-screen viewer
    const allowedLinkKeys    = useAllowedQuickLinks();
    const { searchOptions, quickTasks } = useAllowedSearchAndTasks();
    const canSearchCustomer  = searchOptions.some(o => o.value === 'customer');
    const canSearchInvoice   = searchOptions.some(o => o.value === 'invoice');
    const canSearchInventory = searchOptions.some(o => o.value === 'inventory');

    const user = useContext(UserContext);

    useEffect(() => {
        if (user?.quick_links?.length) {
            setVisibleQuickLinks(user.quick_links);
        } else if (user) {
            setVisibleQuickLinks(ALL_QUICK_LINKS.map(l => l.key));
        }
        setDepartment(user?.department ?? null);
    }, [user]);

    const fetchCustomerSuggestions = (event) => {
        const query = event.query.toLowerCase();
        axiosInstance.get(`/api/customerq?query=${query}`)
            .then(res => {
                setCustomerSuggestions(res.data.map(c => ({ ...c, displayLabel: `${c.first_name} ${c.last_name}` })));
            })
            .catch(handleApiError);
    };

    const fetchInvoiceSuggestions = (event) => {
        const query = event.query.toLowerCase();
        axiosInstance.get(`/api/invoicesq?query=${query}`)
            .then(res => {
                setInvoiceSuggestions(res.data.map(i => ({ ...i, displayLabel: `${i.invoice_number}` })));
            })
            .catch(handleApiError);
    };

    const handleCustomerSelect = (selectedItem) => {
        if (!selectedItem) return;
        navigate(`/customers/${selectedItem.id}`);
    };

    const handleInvoiceSelect = (selectedItem) => {
        if (!selectedItem) return;
        navigate(`/invoices`, { state: { invId: selectedItem.id, customerId: selectedItem.customer } });
    };

    const handleInventorySelect = async (product) => {
        setSelectedProduct(product);
        if (!product) { setSearchResult(null); return; }
        try {
            const res = await axiosInstance.get(`/api/inventory/query/?query=${product.id}`);
            setHasSearched(true);
            setSearchResult(res.data ? { data: res.data } : null);
        } catch (error) {
            console.error("Error fetching inventory data:", error);
            setSearchResult(null);
        }
    };

    const downloadCsv = async (url, fallbackName = "report.csv") => {
        try {
            const res      = await axiosInstance.get(url, { responseType: "blob" });
            const blob     = new Blob([res.data], { type: "text/csv" });
            const cd       = res.headers?.["content-disposition"] || "";
            const m        = cd.match(/filename="?([^"]+)"?/i);
            const filename = (m && m[1]) ? m[1] : fallbackName;
            const link     = document.createElement("a");
            link.href      = URL.createObjectURL(blob);
            link.download  = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            handleApiError(err);
        }
    };

    // Intersection of saved preference AND allowed by permission
    const displayedLinks   = ALL_QUICK_LINKS.filter(link =>
        visibleQuickLinks.includes(link.key) && allowedLinkKeys.has(link.key)
    );
    const customizableLinks = ALL_QUICK_LINKS.filter(link => allowedLinkKeys.has(link.key));

    return (
        <div style={{ padding: '24px' }}>

            {/* ── Quick Task action cards — sit right below the menu bar ───── */}
            {(quickTasks.length > 0 || canViewSummary) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                    {quickTasks.map(task => {
                        const Icon = task.icon;
                        return (
                            <motion.div
                                key={task.key}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => navigate(task.route, { state: task.state })}
                                style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '14px 20px',
                                    minWidth: '170px',
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '10px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                }}
                            >
                                <Icon size={20} />
                                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{task.label}</span>
                            </motion.div>
                        );
                    })}
                    {canViewSummary && (
                        <motion.div
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => setSummaryVisible(true)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '14px 20px',
                                minWidth: '170px',
                                background: '#6366f1',
                                border: '1px solid #6366f1',
                                borderRadius: '10px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                            }}
                        >
                            <BarChart3 size={20} color="#fff" />
                            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Show Summary</span>
                        </motion.div>
                    )}
                </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>

            {/* ── Card 1: Quick Links ───────────────────────────────────────── */}
            <Card
                className="shadow-lg"
                style={{ width: "300px" }}
                title={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                        <span>Quick Links</span>
                        {customizableLinks.length > 0 && (
                            <Button
                                icon={<Cog size={16} />}
                                text
                                onClick={() => { setTempQuickLinks(visibleQuickLinks); setCustomizeVisible(true); }}
                            />
                        )}
                    </div>
                }
            >
                {displayedLinks.length === 0 ? (
                    <p style={{ fontSize: '0.85rem', padding: '8px 0' }}>
                        No quick links available for your role.
                    </p>
                ) : (
                    displayedLinks.map(link => {
                        const Icon = link.icon;
                        return (
                            <motion.div
                                key={link.key}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{ padding: "15px" }}
                            >
                                <Link
                                    to={link.path}
                                    style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "8px" }}
                                >
                                    <Icon size={24} />
                                    <span>{link.label}</span>
                                </Link>
                            </motion.div>
                        );
                    })
                )}
            </Card>

            {/* ── Card 2: Quick Search ─────────────────────────────────────── */}
            <Card title="Quick Search" className="shadow-lg" style={{ width: '450px' }}>

                {searchOptions.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {canSearchCustomer && (
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Customer</label>
                                <AutoComplete
                                    value={customerValue}
                                    suggestions={customerSuggestions}
                                    completeMethod={fetchCustomerSuggestions}
                                    field="displayLabel"
                                    onChange={(e) => setCustomerValue(e.value)}
                                    onSelect={(e) => handleCustomerSelect(e.value)}
                                    placeholder="Search by customer name..."
                                    className="w-full"
                                    style={{ width: '100%', display: 'block' }}
                                    inputStyle={{ width: '100%' }}
                                />
                            </div>
                        )}
                        {canSearchInvoice && (
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Invoice Number</label>
                                <AutoComplete
                                    value={invoiceValue}
                                    suggestions={invoiceSuggestions}
                                    completeMethod={fetchInvoiceSuggestions}
                                    field="displayLabel"
                                    onChange={(e) => setInvoiceValue(e.value)}
                                    onSelect={(e) => handleInvoiceSelect(e.value)}
                                    placeholder="Search by invoice number..."
                                    className="w-full"
                                    style={{ width: '100%', display: 'block' }}
                                    inputStyle={{ width: '100%' }}
                                />
                            </div>
                        )}
                        {canSearchInventory && (
                            <div>
                                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Inventory (Product)</label>
                                <ProductSelectorInput
                                    query={productQuery}
                                    onQueryChange={setProductQuery}
                                    onSelect={handleInventorySelect}
                                    placeholder="Type product name..."
                                    lazy
                                />
                            </div>
                        )}

                        {/* Search result area — inventory lookups only */}
                        {canSearchInventory && (
                            <div style={{ paddingTop: "8px", minHeight: "100px", display: "flex", alignItems: "center" }}>
                                {hasSearched ? (
                                    searchResult ? (
                                        <div>
                                            <p><b>{selectedProduct?.name}</b></p>
                                            <p>
                                                Available Stocks:&nbsp;
                                                {searchResult.data.quantity > 0
                                                    ? searchResult.data.quantity
                                                    : <span style={{ color: "red" }}><b>Out of Stock</b></span>
                                                }
                                            </p>
                                            <p>Price: {searchResult.data.selling_price}</p>
                                        </div>
                                    ) : (
                                        <span style={{ color: "#b91c1c" }}>No results found</span>
                                    )
                                ) : (
                                    <span style={{ color: "#9ca3af" }} />
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    // No search options available for this user's role
                    <div style={{ minHeight: '80px', display: 'flex', alignItems: 'center' }}>
                        <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
                            Search is not available for your role.
                        </p>
                    </div>
                )}
            </Card>

            {/* ── Card 3: Quick Reports ─────────────────────────────────────── */}
            <Card title="Quick Reports" className="shadow-lg" style={{ width: '400px', maxWidth: '100%' }}>

                {!(canDownloadReports || canViewReports) ? (
                    <RestrictedSection />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0 0 6px' }}>Click a report to view it on screen. Use the download icon for a CSV file.</p>
                        {QUICK_REPORTS.map((r) => (
                            <div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Button label={r.cardLabel} link style={{ textAlign: 'left', padding: '6px 0' }} onClick={() => setOpenReport(r)} />
                                {canDownloadReports && (
                                    <Button icon="pi pi-download" rounded text size="small" aria-label={`Download ${r.cardLabel} as CSV`} tooltip="Download CSV" tooltipOptions={{ position: 'left' }}
                                            onClick={() => downloadCsv(`${r.endpoint}?${new URLSearchParams(reportParams(r, { ...defaultChoice(r, null), group: 'detail' })).toString()}`, 'report.csv')} />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <QuickReportViewer key={openReport?.key || "closed"} report={openReport} visible={!!openReport} onHide={() => setOpenReport(null)} canDownload={canDownloadReports} />

            </div>

            {/* SummaryDialog always mounted so the top action-row button can open it */}
            <SummaryDialog
                visible={summaryVisible}
                onHide={() => setSummaryVisible(false)}
                departmentId={department}
            />

            {/* ── Customise Quick Links dialog ──────────────────────────────── */}
            <Dialog
                header="Customize Quick Links"
                visible={customizeVisible}
                style={{ width: "420px" }}
                modal
                onHide={() => setCustomizeVisible(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="Cancel" text onClick={() => setCustomizeVisible(false)} />
                        <Button
                            label="Save"
                            disabled={tempQuickLinks.length === 0}
                            onClick={async () => {
                                try {
                                    await axiosInstance.patch("/api/user/quick-links/", { quick_links: tempQuickLinks });
                                    setVisibleQuickLinks(tempQuickLinks);
                                    const u = JSON.parse(localStorage.getItem("userSettings")) || {};
                                    u.quick_links = tempQuickLinks;
                                    localStorage.setItem("userSettings", JSON.stringify(u));
                                    setCustomizeVisible(false);
                                } catch (err) {
                                    handleApiError(err);
                                }
                            }}
                        />
                    </div>
                }
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    {customizableLinks.map(link => {
                        const Icon    = link.icon;
                        const checked = tempQuickLinks.includes(link.key);
                        return (
                            <div
                                key={link.key}
                                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer' }}
                                onClick={() =>
                                    setTempQuickLinks(prev =>
                                        prev.includes(link.key)
                                            ? prev.filter(k => k !== link.key)
                                            : [...prev, link.key]
                                    )
                                }
                            >
                                <Checkbox checked={checked} readOnly />
                                <Icon size={20} />
                                <span>{link.label}</span>
                            </div>
                        );
                    })}
                </div>
            </Dialog>
        </div>
    );
};

export default Home;